import { z } from "zod";
import { day, inWholeCents, optional, paymentStatus, storableText } from "./appointments";
import { recordId } from "./contracts";
import { blankAsNull, flag, searchTextUpTo, unset } from "./fields";

export const CONTRACT_PAGE_SIZE_MAX = 1000;

export const CONTRACT_SEARCH_MAX_LENGTH = 200;

export const contractStatus = z.enum(["active", "paused", "cancelled"]);
export type ContractStatus = z.infer<typeof contractStatus>;

export const contractProperty = z.object({
  id: recordId,
  name: z.string(),
  address: z.string().nullable(),
  image: z.string().nullable(),
});
export type ContractProperty = z.infer<typeof contractProperty>;

export const contract = z.object({
  id: recordId,
  status: contractStatus,
  start: z.string(),
  end: z.string().nullable(),
  property: contractProperty,
  vendor: z.object({ id: recordId, name: z.string() }),
  monthlyPrice: z.number().nullable().optional(),
  createdAt: z.string(),
});
export type Contract = z.infer<typeof contract>;

export const contractReadQuery = z.object({
  actingOrganization: optional(recordId.toLowerCase()),
});
export type ContractReadQuery = z.infer<typeof contractReadQuery>;

export const contractSortKey = z.enum(["createdAt", "property", "start", "end", "monthlyPrice"]);
export type ContractSortKey = z.infer<typeof contractSortKey>;

export const contractListQuery = contractReadQuery.extend({
  status: optional(contractStatus),
  property: optional(recordId.toLowerCase()),
  search: z.preprocess((value) => (typeof value === "string" ? unset(value.trim()) : value), searchTextUpTo(CONTRACT_SEARCH_MAX_LENGTH).optional()),
  inEffectFrom: optional(day),
  inEffectTo: optional(day),
  sort: z.preprocess(unset, contractSortKey.default("createdAt")),
  order: z.preprocess(unset, z.enum(["asc", "desc"]).default("desc")),
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(CONTRACT_PAGE_SIZE_MAX).default(25)),
});
export type ContractListQuery = z.infer<typeof contractListQuery>;

export const contractPage = z.object({
  items: z.array(contract),
  total: z.number().int().nonnegative(),
});
export type ContractPage = z.infer<typeof contractPage>;

export const CONTRACT_MONTHLY_PRICE_MAX = 99_999_999.99;

const monthlyPrice = z.number().min(0).max(CONTRACT_MONTHLY_PRICE_MAX).refine(inWholeCents, "at most two decimal places").nullable();

export const contractCreateRequest = z
  .object({
    property: recordId.toLowerCase(),
    start: day,
    end: day.nullable().optional(),
    monthlyPrice: monthlyPrice.optional(),
    status: contractStatus.optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type ContractCreateRequest = z.infer<typeof contractCreateRequest>;

export const contractDetailsRequest = z
  .object({
    status: contractStatus.optional(),
    start: day.optional(),
    end: day.nullable().optional(),
    monthlyPrice: monthlyPrice.optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type ContractDetailsRequest = z.infer<typeof contractDetailsRequest>;

export const contractHistoryQuery = contractReadQuery;
export type ContractHistoryQuery = z.infer<typeof contractHistoryQuery>;

export const contractHistoryType = z.enum(["created", "updated", "deleted", "assigned", "unassigned", "on_site", "off_site", "task_completed", "status_changed"]);
export type ContractHistoryType = z.infer<typeof contractHistoryType>;

export const contractHistoryEntry = z.object({
  id: recordId,
  type: contractHistoryType.nullable(),
  message: z.string(),
  author: z.object({ id: recordId, name: z.string().nullable() }).nullable(),
  at: z.string(),
  periodInvoice: z
    .object({
      invoiceId: recordId.nullable(),
      amount: z.number().nullable(),
      daysCompleted: z.number().nullable(),
      hoursWorked: z.number().nullable(),
    })
    .optional(),
  periodInvoiceError: z.string().optional(),
});
export type ContractHistoryEntry = z.infer<typeof contractHistoryEntry>;

export const contractHistory = z.object({ items: z.array(contractHistoryEntry) });
export type ContractHistory = z.infer<typeof contractHistory>;

export const scheduleSyncRequest = z.object({ actingOrganization: optional(recordId.toLowerCase()) }).strict();
export type ScheduleSyncRequest = z.infer<typeof scheduleSyncRequest>;

const dayCount = z.number().int().nonnegative();

export const scheduleSync = z.object({
  frozen: z.boolean(),
  added: dayCount,
  updated: dayCount,
  removed: dayCount,
  keptLocked: dayCount,
  keptDetached: dayCount,
  notified: dayCount,
  window: z.object({ start: z.string(), end: z.string() }).nullable(),
});
export type ScheduleSync = z.infer<typeof scheduleSync>;

export const WORK_DAY_PAGE_SIZE_MAX = 1000;

export const WORK_DAY_SEARCH_MAX_LENGTH = 200;

export const workDayStatus = z.enum(["pending", "assigned", "accepted", "in_progress", "completed"]);
export type WorkDayStatus = z.infer<typeof workDayStatus>;

export const payRateSource = z.enum(["profile", "manual", "none"]);
export type PayRateSource = z.infer<typeof payRateSource>;

export const workDayPay = z.object({
  rate: z.number().nullable(),
  total: z.number().nullable(),
  status: paymentStatus.nullable(),
  source: payRateSource.nullable(),
});
export type WorkDayPay = z.infer<typeof workDayPay>;

export const hoursSource = z.enum(["clock", "scheduled", "manual"]);
export type HoursSource = z.infer<typeof hoursSource>;

export const payoutReview = z.enum(["missing_hours", "invalid_span", "crossed_midnight", "over_scheduled"]);
export type PayoutReview = z.infer<typeof payoutReview>;

export const clockRecordStatus = z.enum(["pending", "in_progress", "completed"]);
export type ClockRecordStatus = z.infer<typeof clockRecordStatus>;

export const clockRecordTask = z.object({ name: z.string(), done: z.boolean(), completedAt: z.string().nullable() });
export type ClockRecordTask = z.infer<typeof clockRecordTask>;

export const clockRecord = z.object({
  id: recordId,
  status: clockRecordStatus,
  onSiteTime: z.string().nullable(),
  offSiteTime: z.string().nullable(),
  tasks: z.array(clockRecordTask).optional(),
  notes: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ClockRecord = z.infer<typeof clockRecord>;

const named = z.object({ id: recordId, name: z.string() });

export const contractOfDay = contract.omit({ monthlyPrice: true, createdAt: true, status: true });
export type ContractOfDay = z.infer<typeof contractOfDay>;

export const workDay = z.object({
  id: recordId,
  date: z.string(),
  status: workDayStatus,
  contract: contractOfDay,
  service: z.object({ id: recordId, name: z.string(), category: z.string().nullable() }),
  tasks: z.array(z.string()),
  assignment: z.object({ subcontractor: named.nullable(), worker: named.nullable() }),
  maxHours: z.number().nullable(),
  durationLabel: z.string().nullable(),
  instructions: z.string().nullable(),
  recurring: z.boolean(),
  detachedFromScheduleAt: z.string().nullable(),
  hours: z.object({ worked: z.number().nullable(), source: hoursSource.nullable(), review: payoutReview.nullable() }),
  pay: z.object({ subcontractor: workDayPay.optional(), worker: workDayPay.optional() }),
  clockRecord: clockRecord.nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type WorkDay = z.infer<typeof workDay>;

export const workDayReadQuery = contractReadQuery;
export type WorkDayReadQuery = z.infer<typeof workDayReadQuery>;

export const workDaySortKey = z.enum(["date", "property"]);
export type WorkDaySortKey = z.infer<typeof workDaySortKey>;

export const workDayListQuery = contractReadQuery.extend({
  contract: optional(recordId.toLowerCase()),
  from: optional(day),
  to: optional(day),
  status: optional(workDayStatus),
  workerPayStatus: optional(paymentStatus),
  subcontractorPayStatus: optional(paymentStatus),
  assignedToMe: z.preprocess(flag, z.boolean().optional()),
  search: z.preprocess((value) => (typeof value === "string" ? unset(value.trim()) : value), searchTextUpTo(WORK_DAY_SEARCH_MAX_LENGTH).optional()),
  sort: z.preprocess(unset, workDaySortKey.default("date")),
  order: z.preprocess(unset, z.enum(["asc", "desc"]).default("asc")),
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(WORK_DAY_PAGE_SIZE_MAX).default(25)),
});
export type WorkDayListQuery = z.infer<typeof workDayListQuery>;

export const workDayPage = z.object({
  items: z.array(workDay),
  total: z.number().int().nonnegative(),
});
export type WorkDayPage = z.infer<typeof workDayPage>;

export const WORK_DAY_RATE_MAX = 10_000;

export const WORK_DAY_TASKS_MAX = 50;

export const WORK_DAY_TASK_MAX_LENGTH = 200;

export const WORK_DAY_HOURS_MAX = 24;

export const OFF_SITE_NOTE_MAX_LENGTH = 1000;

export const workDayAssignment = z.union([z.object({ subcontractor: recordId.toLowerCase() }).strict(), z.object({ worker: recordId.toLowerCase() }).strict()]);
export type WorkDayAssignment = z.infer<typeof workDayAssignment>;

export const workDayRate = z.number().min(0).max(WORK_DAY_RATE_MAX).refine(inWholeCents, "at most two decimal places");

export const workDayTasks = z.array(storableText(WORK_DAY_TASK_MAX_LENGTH)).max(WORK_DAY_TASKS_MAX);

export const workDayHours = z.number().positive().max(WORK_DAY_HOURS_MAX);

export const workDayCreateRequest = z
  .object({
    date: day,
    service: recordId.toLowerCase(),
    tasks: workDayTasks,
    assignment: workDayAssignment.nullable().optional(),
    rate: workDayRate.optional(),
    maxHours: workDayHours.nullable().optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type WorkDayCreateRequest = z.infer<typeof workDayCreateRequest>;

export const workDayDetailsRequest = z
  .object({
    service: recordId.toLowerCase().optional(),
    tasks: workDayTasks.optional(),
    assignment: workDayAssignment.nullable().optional(),
    rate: workDayRate.optional(),
    maxHours: workDayHours.nullable().optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type WorkDayDetailsRequest = z.infer<typeof workDayDetailsRequest>;

export const workDayDeleteRequest = contractReadQuery.strict();
export type WorkDayDeleteRequest = z.infer<typeof workDayDeleteRequest>;

export const payoutHoursRequest = z
  .object({
    hours: workDayHours,
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type PayoutHoursRequest = z.infer<typeof payoutHoursRequest>;

export const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, "expected HH:mm");

export const offSiteRequest = z
  .object({
    offSiteTime: timeOfDay,
    note: z.preprocess((value) => (typeof value === "string" && value.trim() === "" ? null : value), storableText(OFF_SITE_NOTE_MAX_LENGTH).nullable().optional()),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type OffSiteRequest = z.infer<typeof offSiteRequest>;

export const CLOCK_RECORD_NOTES_MAX_LENGTH = 5000;

export const workDayActionRequest = contractReadQuery.strict();
export type WorkDayActionRequest = z.infer<typeof workDayActionRequest>;

const clockRecordNotes = z.preprocess(blankAsNull, storableText(CLOCK_RECORD_NOTES_MAX_LENGTH).nullable());

export const clockOutRequest = z
  .object({
    notes: clockRecordNotes.optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type ClockOutRequest = z.infer<typeof clockOutRequest>;

export const clockRecordNotesRequest = z
  .object({
    notes: clockRecordNotes,
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type ClockRecordNotesRequest = z.infer<typeof clockRecordNotesRequest>;

export const clockRecordTaskRequest = z
  .object({
    done: z.boolean(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type ClockRecordTaskRequest = z.infer<typeof clockRecordTaskRequest>;

export const workerAssignmentRequest = z
  .object({
    worker: recordId.toLowerCase(),
    rate: workDayRate.optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type WorkerAssignmentRequest = z.infer<typeof workerAssignmentRequest>;
